import {
  FormCard,
  FormItem,
  Select,
  Input,
  TextArea,
  MultiSelect,
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import { FC } from 'react'
import { DefaultOptionType } from 'antd/es/select'
import { getRequiredRule, getTeamMembersClockInsRule } from '../utils'
import { InputNumber } from 'antd'
import { cleanDecimalValues } from './utils'
import InputSuffix from './InputSuffix'

import PatternSelectEmptyState from '../components/PatternSelectEmptyState'
import { useGroupedCarers } from 'hooks/useGroupedOptions'
import { useCarersClockedInValidator } from '../hooks/useCarersClockedInValidator'
import useFeatureFlags from 'hooks/useFeatureFlags'

type AboutVisitProps = {
  typeOfVisitOptions?: DefaultOptionType[]
  hideTeamMembers?: boolean
  hideAboutSection?: boolean
  showTeamMembersSelect?: boolean
  formData?: Record<string, any> | undefined
}

const AboutVisit: FC<AboutVisitProps> = ({
  typeOfVisitOptions,
  hideTeamMembers,
  hideAboutSection,
  showTeamMembersSelect,
  formData,
}) => {
  const { t } = useTranslation()
  const { groupedCarerOptions } = useGroupedCarers()

  const flags = useFeatureFlags({
    flagNames: ['rostering_editing_limitations'],
  })?.data

  const flagsRosteringEditingLimitations =
    flags?.rostering_editing_limitations?.enabled

  const teamMembersCount = formData?.teamMembers?.length ?? 0
  const maxTeamMembers = formData?.numberSupply ?? 0

  const { validator: validateTeamMembers } = useCarersClockedInValidator(
    formData?.id
  )

  return (
    <FormCard id="rota-about-visit-form" level={2} title={t('ABOUT_THE_VISIT')}>
      {!hideAboutSection && (
        <>
          <FormItem
            name="instanceTypeId"
            label={<Text>{t('TYPE_OF_VISIT')}</Text>}
            rules={[getRequiredRule(t('TYPE_OF_VISIT'))]}
          >
            <Select
              options={typeOfVisitOptions}
              notFoundContent={
                <PatternSelectEmptyState link="/settings/rota-patterns" />
              }
            />
          </FormItem>
          <FormItem
            name="title"
            label={<Text>{t('TITLE')}</Text>}
            rules={[getRequiredRule(t('TITLE'))]}
          >
            <Input />
          </FormItem>
          <FormItem name="slotNotes" label={<Text>{t('DESCRIPTION')}</Text>}>
            <TextArea maxLength={250} showCount />
          </FormItem>
        </>
      )}
      {!hideTeamMembers && (
        <FormItem
          name="numberSupply"
          label={<Text>{t('TEAM_MEMBERS_REQUIRED')}</Text>}
          rules={[
            getRequiredRule(t('TEAM_MEMBERS_REQUIRED')),
            ...(flagsRosteringEditingLimitations
              ? [
                  {
                    ...getTeamMembersClockInsRule(
                      formData?.status,
                      formData?.clockIns
                    ),
                    validateTrigger: 'onSubmit',
                  },
                ]
              : []),
          ]}
          extra={<Text>{t('TEAM_MEMBERS_REQUIRED_SELECT')}</Text>}
        >
          <InputNumber
            suffix={
              <InputSuffix>{t('TEAM_MEMBERS').toLocaleLowerCase()}</InputSuffix>
            }
            min={1}
            max={50}
            style={{ width: '100%' }}
            type="number"
            formatter={cleanDecimalValues}
          />
        </FormItem>
      )}
      {showTeamMembersSelect && (
        <FormItem
          name="teamMembers"
          rules={[
            ...(flagsRosteringEditingLimitations
              ? [
                  {
                    validator: validateTeamMembers,
                    validateTrigger: 'onSubmit',
                  },
                ]
              : []),
          ]}
          label={<Text>{t('TEAM_MEMBER(S)')}</Text>}
        >
          <MultiSelect
            title={t('SELECT_TEAM_MEMBERS')}
            groupedOptions={groupedCarerOptions}
            hasClear
            hasSearch
            hasSelectAll
            placeholder
            maxTagCount={null}
            values={formData?.teamMembers || []}
            suffixIcon={
              <Text>
                {teamMembersCount}/{maxTeamMembers}
              </Text>
            }
          />
        </FormItem>
      )}
    </FormCard>
  )
}

export default AboutVisit
